import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { walkiesClient } from "../api/walkiesClient";

type SubmitState = "idle" | "submitting" | "submitted" | "error";

function normalizeReturnUrl(raw?: string | null): string {
  if (!raw) return "/walker/dashboard";

  const trimmed = raw.trim();
  if (!trimmed) return "/walker/dashboard";

  if (!trimmed.startsWith("/")) return "/walker/dashboard";
  if (trimmed.startsWith("//")) return "/walker/dashboard";

  return trimmed;
}

const WalkerAccessRequestPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const returnUrl = useMemo(
    () => normalizeReturnUrl(searchParams.get("returnUrl")),
    [searchParams]
  );

  const [email, setEmail] = useState("");
  const [state, setState] = useState<SubmitState>("idle");
  const [message, setMessage] = useState("");
  const [checkingExistingSession, setCheckingExistingSession] = useState(true);

  useEffect(() => {
    async function checkExistingSession() {
      try {
        await walkiesClient.getWalkerMe();
        navigate(returnUrl, { replace: true });
      } catch {
        setCheckingExistingSession(false);
      }
    }

    void checkExistingSession();
  }, [navigate, returnUrl]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    if (!email.trim()) {
      setState("error");
      setMessage("Please enter your email address.");
      return;
    }

    try {
      setState("submitting");
      setMessage("");

      const result = await walkiesClient.requestWalkerMagicLink({
        email: email.trim(),
      });

      setState("submitted");
      setMessage(
        result?.message ||
          "We’ve sent you a secure access link. Please check your email."
      );
    } catch (err) {
      setState("error");
      setMessage(
        err instanceof Error ? err.message : "Failed to request access link."
      );
    }
  }

  if (checkingExistingSession) {
    return (
      <div style={styles.page}>
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.badge}>Walker access</div>
            <h1 style={styles.title}>Checking your secure session…</h1>
            <p style={styles.text}>
              Please wait while we check whether you are already signed in.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.badge}>Walker access</div>
          <h1 style={styles.title}>Open your walker dashboard</h1>
          <p style={styles.text}>
            Enter the email you used for your WalkiesNearMe walker profile and we’ll
            send you a secure access link.
          </p>

          {state === "submitted" && <div style={styles.success}>{message}</div>}
          {state === "error" && <div style={styles.error}>{message}</div>}

          <form onSubmit={handleSubmit} style={styles.form}>
            <label htmlFor="email" style={styles.label}>
              Email
            </label>

            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);

                if (state !== "idle") {
                  setState("idle");
                  setMessage("");
                }
              }}
              style={styles.input}
              placeholder="you@example.com"
              required
            />

            <button
              type="submit"
              style={styles.button}
              disabled={state === "submitting"}
            >
              {state === "submitting" ? "Sending link..." : "Send secure access link"}
            </button>
          </form>

          <div style={styles.links}>
            <button
              type="button"
              style={styles.secondaryButton}
              onClick={() => navigate(returnUrl)}
            >
              Continue
            </button>

            <Link to="/" style={styles.linkButton}>
              Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  page: {
    minHeight: "100vh",
    background:
      "radial-gradient(circle at top right, rgba(242, 138, 22, 0.16), transparent 28%), linear-gradient(180deg, #2f4f46 0%, #213a33 100%)",
    padding: "32px 16px 64px",
    color: "#ffffff",
  },
  container: {
    maxWidth: 760,
    margin: "0 auto",
  },
  card: {
    background: "#f5f0e8",
    color: "#18201d",
    borderRadius: 28,
    padding: 28,
    boxShadow: "0 18px 48px rgba(0, 0, 0, 0.18)",
  },
  badge: {
    display: "inline-flex",
    padding: "8px 12px",
    borderRadius: 999,
    background: "rgba(47, 79, 70, 0.08)",
    fontWeight: 800,
    marginBottom: 16,
  },
  title: {
    margin: "0 0 12px",
    fontSize: 38,
    fontWeight: 900,
    color: "#213a33",
  },
  text: {
    margin: "0 0 20px",
    lineHeight: 1.7,
    color: "rgba(24, 32, 29, 0.8)",
  },
  success: {
    borderRadius: 16,
    padding: 14,
    marginBottom: 16,
    background: "rgba(33, 58, 51, 0.08)",
    border: "1px solid rgba(33, 58, 51, 0.15)",
    fontWeight: 700,
  },
  error: {
    borderRadius: 16,
    padding: 14,
    marginBottom: 16,
    background: "rgba(220, 38, 38, 0.08)",
    border: "1px solid rgba(220, 38, 38, 0.18)",
    fontWeight: 700,
  },
  form: {
    display: "grid",
    gap: 10,
  },
  label: {
    fontWeight: 800,
    color: "#213a33",
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    padding: "14px",
    borderRadius: 14,
    border: "1px solid rgba(24, 32, 29, 0.14)",
    outline: "none",
    font: "inherit",
  },
  button: {
    marginTop: 10,
    border: "none",
    borderRadius: 16,
    padding: "14px",
    background: "#f28a16",
    color: "#fff",
    fontWeight: 800,
    cursor: "pointer",
    font: "inherit",
  },
  links: {
    marginTop: 18,
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    alignItems: "center",
  },
  secondaryButton: {
    padding: "12px 16px",
    borderRadius: 16,
    background: "#213a33",
    color: "#fff",
    fontWeight: 800,
    border: "none",
    cursor: "pointer",
    font: "inherit",
  },
  linkButton: {
    color: "#213a33",
    fontWeight: 800,
    textDecoration: "none",
  },
};

export default WalkerAccessRequestPage;